import { memo, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import type { InvoiceCategory } from "../types/invoice.types";
import type { InvoiceListFilters } from "../hooks/useInvoiceList";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

interface InvoiceFiltersProps {
  category: InvoiceCategory;
  filters: InvoiceListFilters;
  loading: boolean;
  onApply: (filters: InvoiceListFilters) => void;
  onClear: () => void;
}

export const InvoiceFilters = memo(function InvoiceFilters({
  category,
  filters,
  loading,
  onApply,
  onClear,
}: InvoiceFiltersProps) {
  const [draft, setDraft] = useState<InvoiceListFilters>(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const isRental = category === "RENTAL";
  const referenceIdValue = isRental
    ? (draft.rentalId ?? "")
    : (draft.graduationId ?? "");

  const handleReferenceIdChange = (raw: string) => {
    const parsed = raw === "" ? undefined : Number(raw);
    if (parsed !== undefined && Number.isNaN(parsed)) return;
    setDraft((prev) =>
      isRental
        ? { ...prev, rentalId: parsed }
        : { ...prev, graduationId: parsed },
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label htmlFor="invoice-filter-external-id">ID Externo</Label>
        <Input
          id="invoice-filter-external-id"
          placeholder="EXT-2024-001"
          value={draft.externalId ?? ""}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              externalId: e.target.value || undefined,
            }))
          }
          disabled={loading}
        />
      </div>

      {category !== "MANUAL" && (
        <div className="space-y-2">
          <Label htmlFor="invoice-filter-reference-id">
            {isRental ? "ID da Locação" : "ID da Formatura"}
          </Label>
          <Input
            id="invoice-filter-reference-id"
            type="number"
            min="1"
            placeholder="123"
            value={referenceIdValue}
            onChange={(e) => handleReferenceIdChange(e.target.value)}
            disabled={loading}
          />
        </div>
      )}

      {isRental && (
        <div className="space-y-2">
          <Label htmlFor="invoice-filter-contract-number">Nº do Contrato</Label>
          <Input
            id="invoice-filter-contract-number"
            placeholder="Ex.: 12345"
            value={draft.contractNumber ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                contractNumber: e.target.value || undefined,
              }))
            }
            disabled={loading}
          />
        </div>
      )}

      {category !== "MANUAL" && (
        <div className="space-y-2">
          <Label htmlFor="invoice-filter-client-name">Cliente</Label>
          <Input
            id="invoice-filter-client-name"
            placeholder="Nome do cliente"
            value={draft.clientName ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                clientName: e.target.value || undefined,
              }))
            }
            disabled={loading}
          />
        </div>
      )}

      {category !== "MANUAL" && (
        <div className="space-y-2">
          <Label htmlFor="invoice-filter-client-document">
            Documento do cliente
          </Label>
          <Input
            id="invoice-filter-client-document"
            placeholder="CPF ou CNPJ"
            value={draft.clientDocument ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                clientDocument: e.target.value || undefined,
              }))
            }
            disabled={loading}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="invoice-filter-value">Valor (R$)</Label>
        <Input
          id="invoice-filter-value"
          type="number"
          step="0.01"
          min="0"
          placeholder="0,00"
          value={draft.value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = raw === "" ? undefined : Number(raw);
            if (parsed !== undefined && Number.isNaN(parsed)) return;
            setDraft((prev) => ({ ...prev, value: parsed }));
          }}
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="invoice-filter-status">Status</Label>
        <select
          id="invoice-filter-status"
          className={SELECT_CLASSES}
          value={draft.status ?? ""}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              status:
                e.target.value === ""
                  ? undefined
                  : (e.target.value as InvoiceListFilters["status"]),
            }))
          }
          disabled={loading}
        >
          <option value="">Todos</option>
          <option value="PENDING">Pendente</option>
          <option value="ISSUED">Emitida</option>
          <option value="ERROR">Erro</option>
          <option value="WAITING">Aguardando</option>
          <option value="CANCELED">Cancelada</option>
          <option value="EMISSION_DISABLED">Emissão desativada</option>
          <option value="EMISSION_SKIPPED">Emissão não solicitada</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="invoice-filter-type">Tipo</Label>
        <select
          id="invoice-filter-type"
          className={SELECT_CLASSES}
          value={draft.type ?? ""}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              type:
                e.target.value === ""
                  ? undefined
                  : (e.target.value as InvoiceListFilters["type"]),
            }))
          }
          disabled={loading}
        >
          <option value="">Todos</option>
          <option value="PAYMENT">Pagamento</option>
          <option value="CANCELLATION">Cancelamento</option>
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="invoice-filter-payment-method">Pagamento</Label>
        <select
          id="invoice-filter-payment-method"
          className={SELECT_CLASSES}
          value={draft.paymentMethod ?? ""}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              paymentMethod:
                e.target.value === ""
                  ? undefined
                  : (e.target.value as InvoiceListFilters["paymentMethod"]),
            }))
          }
          disabled={loading}
        >
          <option value="">Todos</option>
          <option value="PIX">PIX</option>
          <option value="CREDIT_CARD">Crédito</option>
          <option value="DEBIT_CARD">Débito</option>
          <option value="CASH">Dinheiro</option>
        </select>
      </div>

      <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClear}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Limpar
        </Button>
        <Button
          onClick={() => onApply(draft)}
          disabled={loading}
          className="gap-2"
        >
          <Search className="w-4 h-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
});
